/**
 * Resolves which prompt layers (P4/P5/P6) the current user may view or edit.
 * Org admins remain P3-only; hierarchy fields gate P4–P6 editors.
 */

const asTrimmed = (value) => (typeof value === "string" ? value.trim() : "");

export const PROMPT_LAYER_LABELS = {
  1: "Global",
  2: "Regional",
  3: "Institution-wide",
  4: "Sub-institution",
  5: "Department",
  6: "Sub-department",
};

export function resolveHierarchyPromptEditorAccess({
  userData = {},
  isGlobalAdmin = false,
  isOrgAdmin = false,
  isRegionalAdmin = false,
  isFdeT = false,
  organizationId = null,
}) {
  const institutionId = asTrimmed(
    userData.activeInstitutionId ||
      organizationId ||
      userData.institutionId ||
      userData.organizationId,
  );
  const subInstitutionId = asTrimmed(userData.subInstitutionId);
  const departmentId = asTrimmed(userData.departmentId);
  const subDepartmentId = asTrimmed(userData.subDepartmentId);
  const regionId = asTrimmed(userData.regionId || userData.region || userData.geographicRegion);

  if (isGlobalAdmin) {
    return {
      canView: true,
      canEditGlobal: true,
      canEditRegional: true,
      canEditOrganization: true,
      canEditP4: true,
      canEditP5: true,
      canEditP6: true,
      institutionId: institutionId || null,
      regionId: regionId || null,
      p4ScopeId: subInstitutionId || null,
      p5ScopeId: departmentId || null,
      p6ScopeId: subDepartmentId || null,
      editableLayers: [1, 2, 3, 4, 5, 6],
    };
  }

  if (isRegionalAdmin && regionId) {
    return {
      canView: true,
      canEditGlobal: false,
      canEditRegional: true,
      canEditOrganization: false,
      canEditP4: false,
      canEditP5: false,
      canEditP6: false,
      institutionId: null,
      regionId,
      p4ScopeId: null,
      p5ScopeId: null,
      p6ScopeId: null,
      editableLayers: [2],
    };
  }

  if (isOrgAdmin && institutionId) {
    return {
      canView: true,
      canEditGlobal: false,
      canEditRegional: false,
      canEditOrganization: true,
      canEditP4: false,
      canEditP5: false,
      canEditP6: false,
      institutionId,
      regionId: null,
      p4ScopeId: null,
      p5ScopeId: null,
      p6ScopeId: null,
      editableLayers: [3],
    };
  }

  if (subDepartmentId && institutionId) {
    return {
      canView: true,
      canEditGlobal: false,
      canEditRegional: false,
      canEditOrganization: false,
      canEditP4: false,
      canEditP5: false,
      canEditP6: true,
      institutionId,
      regionId: null,
      p4ScopeId: null,
      p5ScopeId: null,
      p6ScopeId: subDepartmentId,
      editableLayers: [6],
    };
  }

  if (departmentId && institutionId) {
    return {
      canView: true,
      canEditGlobal: false,
      canEditRegional: false,
      canEditOrganization: false,
      canEditP4: false,
      canEditP5: true,
      canEditP6: false,
      institutionId,
      regionId: null,
      p4ScopeId: null,
      p5ScopeId: departmentId,
      p6ScopeId: null,
      editableLayers: [5],
    };
  }

  if (subInstitutionId && institutionId) {
    return {
      canView: true,
      canEditGlobal: false,
      canEditRegional: false,
      canEditOrganization: false,
      canEditP4: true,
      canEditP5: false,
      canEditP6: false,
      institutionId,
      regionId: null,
      p4ScopeId: subInstitutionId,
      p5ScopeId: null,
      p6ScopeId: null,
      editableLayers: [4],
    };
  }

  if (isFdeT) {
    return {
      canView: true,
      canEditGlobal: false,
      canEditRegional: false,
      canEditOrganization: false,
      canEditP4: false,
      canEditP5: false,
      canEditP6: false,
      institutionId: institutionId || null,
      regionId: null,
      p4ScopeId: null,
      p5ScopeId: null,
      p6ScopeId: null,
      editableLayers: [],
    };
  }

  return {
    canView: false,
    canEditGlobal: false,
    canEditRegional: false,
    canEditOrganization: false,
    canEditP4: false,
    canEditP5: false,
    canEditP6: false,
    institutionId: null,
    regionId: null,
    p4ScopeId: null,
    p5ScopeId: null,
    p6ScopeId: null,
    editableLayers: [],
  };
}

export function getHierarchyPromptDocId({ level, scopeId, promptId }) {
  return `${level}__${scopeId}__${promptId}`;
}
